/*
 * PdfDialog3.tsx
 *
 * Created by Dr. Maximillian Dornseif 2022-02-18 in huWaWi3
 * Copyright (c) 2022-2024 HUDORA GmbH
 */

import { ICommandBarItemProps } from '@fluentui/react'
import { IComboBoxOption } from '@fluentui/react/lib/ComboBox'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import carboneSDK from 'carbone-sdk-js'
import { saveAs } from 'file-saver'
import gql from 'graphql-tag'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useQuery } from 'urql'

import { HdEditIcon } from '../lib/components/HdIcons'
import { HdSpinner } from '../lib/components/widgets/HdSpinner'
import { IEntity } from '../types'
import { useHdDialog } from '../WaWi/components/HdDialog'

export const carbone = carboneSDK(process.env.REACT_APP_CARBONE_API_KEY)
carbone.setApiVersion(4)

const toOptions: IComboBoxOption[] = [
  { key: 'pdf', text: 'PDF' },
  { key: 'docx', text: 'DOCX' },
  { key: 'odt', text: 'ODT' },
  { key: 'txt', text: 'TXT' },
  { key: 'xml', text: 'XML' },
  { key: 'pptx', text: 'PPTX' },
  { key: 'xlxs', text: 'XLXS' },
  { key: 'ods', text: 'ODS' },
  { key: 'csv', text: 'CSV' },
]

const TEMPLATE_QUERY = gql`
  query ConfigTemplateQuery3($cursor: String) {
    nodes: h3ConfigTemplatePages(after: $cursor, limit: 1000) {
      edges {
        cursor
        node {
          designator
          art
          templateId
          ist_aktiv
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

export interface IPdfDialog3Options {
  entity: IEntity
}

/** PDF on demand erstellen
 */
export function usePdfDialog3(options: IPdfDialog3Options) {
  const { openDialog, renderDialog, isDialogOpen } = useHdDialog('PDF rendern')
  const [renderError, setRenderError] = useState(null)
  const [rendering, setRendering] = useState(false)
  const [toOption, setToOption] = useState('pdf')
  const [templateOption, setTemplateOption] = useState(
    '4b9c6528d085c978808f2411ae4cb77a71df9f8d72fb0206d1f3166847a46afc'
  )
  const [{ fetching, data }] = useQuery({
    query: TEMPLATE_QUERY,
    // variables: { belegart: options.entity.belegart },
    pause: !isDialogOpen,
  })
  useEffect(() => {
    // Passenden templateID finden
    for (const x of data?.nodes?.edges || []) {
      if (x.node.art === options.entity.belegart) {
        setTemplateOption(x.node.templateId)
      }
    }
  }, [fetching, data, options.entity.belegart])
  console.log(toOption, templateOption)

  const renderOpenButton = useCallback(
    (e?: IEntity): ReactElement => (
      <IconButton size="small" color="primary" title="Bearbeiten" onClick={() => openDialog()}>
        <HdEditIcon />
      </IconButton>
    ),
    [openDialog]
  )

  if (!options.entity) {
    return [null, null, null, null]
  }

  async function startRender() {
    let fileName = `${options.entity.designator}-${options.entity.belegart}`
    const prom = async () => {
      setRendering(true)
      setRenderError(null)
      // Das sollte wie huwawi3-py/components/belege/render.py funktionieren
      const carboneReply = await carbone.render(templateOption, {
        data: options.entity,
        convertTo: toOption || 'pdf',
        lang: 'de-de',
        currency: {
          source: 'EUR',
          target: 'EUR',
          rates: {
            EUR: 1,
            CENT: 0.01,
          },
        },
      })
      fileName = `${options.entity.designator}-${options.entity.belegart}-${carboneReply.name}`
      saveAs(carboneReply.content, fileName)
      setRendering(false)
    }

    toast.promise(prom(), {
      loading: `Generiere ${fileName} / ${toOption || 'pdf'}`,
      success: () => <React.Fragment>Erfolgreich {fileName} erzeugt und herunter geladen.</React.Fragment>,
      error: (e) => {
        console.error(e)
        setRenderError(e)
        setRendering(false)
        return `Fehler: ${e}`
      },
    })
  }

  const commandBarItem: ICommandBarItemProps = {
    key: 'pdfrender',
    text: 'Beleg neu rendern',
    title: 'Beleg neu rendern',
    ariaLabel: 'Beleg neu rendern',
    iconProps: { iconName: 'PenWorkspace' },
    iconOnly: false,
    onClick: openDialog,
  }

  const thePanel = renderDialog({
    renderBody: () => (
      <Box>
        <form>
          <Stack spacing={3}>
            <Box
              component="form"
              sx={{
                '& > :not(style)': { m: 1, width: '90%' },
              }}
              noValidate
              autoComplete="off"
            >
              <TextField
                label="Rendering Template"
                value={templateOption}
                placeholder="carbone.io templateID nicht konfiguriert"
                onChange={(event) => {
                  console.log(event, event.target.value)
                  setTemplateOption(event.target.value)
                }}
              />
              <Select
                value={toOption || 'pdf'}
                label="Erstelle Rendering im Format"
                onChange={(event) => {
                  setToOption(event.target.value as string)
                }}
              >
                {toOptions.map((option) => (
                  <MenuItem value={option.key}>{option.text}</MenuItem>
                ))}
              </Select>
            </Box>
          </Stack>
        </form>
        <Divider />

        {renderError ? <p>{`Fehler: ${renderError}`}</p> : null}
      </Box>
    ),
    renderActions: () => (
      <>
        {' '}
        {rendering ? <HdSpinner /> : null}
        <Button variant="contained" onClick={startRender} disabled={!!rendering || !templateOption}>
          Rendern
        </Button>
      </>
    ),
  })

  return [commandBarItem, thePanel, openDialog, renderOpenButton]
}
